import React, { useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useAuditActions } from '../hooks/useAudit';
import { 
  Download, 
  Upload, 
  AlertTriangle, 
  XCircle,
  Shield
} from 'lucide-react';

interface DataImportExportProps {
  onExport?: (format: string, scope?: string) => void;
  onImport?: (file: File, mode: 'create' | 'update' | 'delete') => void;
}

const DataImportExport: React.FC<DataImportExportProps> = ({
  onExport,
  onImport
}) => {
  const permissions = usePermissions();
  const auditActions = useAuditActions();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'create' | 'update' | 'delete'>('create');
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel' | 'json'>('csv');
  const [showSensitiveData, setShowSensitiveData] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !permissions.canImportCreate) return;

    try {
      // Logger l'action d'import
      await auditActions.logImport('ITEM', `import_${Date.now()}`, {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        mode: importMode,
        timestamp: new Date().toISOString()
      });

      onImport?.(selectedFile, importMode);
      setSelectedFile(null);
    } catch (error) {
      console.error('Erreur lors de l\'import:', error);
    }
  };

  const handleExport = async () => {
    if (!permissions.canExport) return;

    try {
      // Logger l'action d'export
      await auditActions.logExport('ITEM', `export_${Date.now()}`);
      
      onExport?.(exportFormat);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
    }
  };


  const getImportModeDescription = (mode: string) => {
    switch (mode) {
      case 'create': return 'Ajouter de nouveaux éléments uniquement';
      case 'update': return 'Modifier les éléments existants';
      case 'delete': return 'Supprimer les éléments spécifiés';
      default: return '';
    }
  };


  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-4">
        <Shield className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Import/Export de Données</h3>
      </div>

      {/* Guide d'utilisation */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">📘 Guide d'utilisation</h4>
        <div className="space-y-2 text-sm text-blue-800">
          <p><strong>Export :</strong> Téléchargez vos données au format CSV, Excel ou JSON</p>
          <p><strong>Import :</strong> Importez des données en masse (objets, lieux) depuis un fichier CSV</p>
          <div className="mt-3 space-y-1 text-xs">
            <p><strong>Données exportables/importables :</strong></p>
            <ul className="list-disc list-inside ml-2 space-y-0.5">
              <li><strong>Objets :</strong> nom, catégorie, quantité, état, zone, box, photos, etc.</li>
              <li><strong>Lieux :</strong> sites, bâtiments, pièces, zones, boxes</li>
              <li><strong>Format CSV requis :</strong> voir modèle téléchargeable ci-dessous</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section Export */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Download className="h-5 w-5 text-green-600" />
            <h4 className="text-md font-semibold text-gray-900">Export</h4>
          </div>

          {permissions.canExport ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Format d'export
                </label>
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel' | 'json')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="csv">CSV</option>
                  <option value="excel">Excel</option>
                  <option value="json">JSON</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="showSensitiveData"
                  checked={showSensitiveData}
                  onChange={(e) => setShowSensitiveData(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="showSensitiveData" className="text-sm text-gray-700">
                  Inclure les données sensibles
                </label>
              </div>

              <button
                onClick={handleExport}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>Exporter les Données</span>
              </button>

              {/* Lien modèle CSV */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-600 mb-2">📄 Besoin d'un modèle ?</p>
                <a
                  href="/modeles/import_objets.csv"
                  download
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Télécharger le modèle CSV pour objets
                </a>
                <br />
                <a
                  href="/modeles/import_lieux.csv"
                  download
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Télécharger le modèle CSV pour lieux
                </a>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center space-x-2">
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-800">
                  Vous n'avez pas l'autorisation d'exporter des données.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Section Import */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Upload className="h-5 w-5 text-blue-600" />
            <h4 className="text-md font-semibold text-gray-900">Import</h4>
          </div>

          {permissions.canImportCreate || permissions.canImportUpdate || permissions.canImportDelete ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mode d'import
                </label>
                <select
                  value={importMode}
                  onChange={(e) => setImportMode(e.target.value as 'create' | 'update' | 'delete')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={!permissions.canImportCreate && !permissions.canImportUpdate && !permissions.canImportDelete}
                >
                  {permissions.canImportCreate && <option value="create">Création seule</option>}
                  {permissions.canImportUpdate && <option value="update">Mise à jour</option>}
                  {permissions.canImportDelete && <option value="delete">Suppression</option>}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {getImportModeDescription(importMode)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier à importer
                </label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,.json"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600 mt-1">
                    Fichier sélectionné: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <button
                onClick={handleImport}
                disabled={!selectedFile}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Upload className="h-4 w-4" />
                <span>Importer les Données</span>
              </button>
            </div>
          ) : (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center space-x-2">
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-800">
                  Vous n'avez pas l'autorisation d'importer des données.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Avertissements de sécurité */}
      <div className="mt-6 space-y-3">
        {permissions.canExport && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <span className="text-sm text-yellow-800">
                <strong>Attention:</strong> L'export de données sensibles est tracé et audité.
              </span>
            </div>
          </div>
        )}

        {permissions.canImportDelete && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-800">
                <strong>Attention:</strong> Le mode suppression peut supprimer définitivement des données.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataImportExport;
